import * as yup from 'yup'
import {
    DAYS_OF_WEEK,
    MEMBER_TIERS,
    VrboDiscountMode,
    VrboDiscountUnit,
    VrboDiscountValue,
    VrboPromotion,
    VrboPromotionFormValues,
} from "./vrboPromotionTypes"

const isBlank = (value: any) => value === '' || value === null || value === undefined

export function emptyPromotionFormValues(): VrboPromotionFormValues {
    return {
        promotionType: '',
        status: 'ACTIVE',
        discountMode: 'single',
        discountUnit: 'PERCENT',
        singleValue: '',
        dayOfWeek: { monday: '', tuesday: '', wednesday: '', thursday: '', friday: '', saturday: '', sunday: '' },
        tiers: { BLUE: '', SILVER: '', GOLD: '', PLATINUM: '' },
        travelFrom: '',
        travelTo: '',
        blackoutDates: [],
        stayMin: '',
        stayMax: '',
        advanceMin: '',
        advanceMax: '',
        bookingFrom: '',
        bookingTo: '',
        useJson: false,
        jsonText: '',
        extras: {},
    }
}

// VRBO returns "2026-10-20T00:00:00"; datetime-local inputs want "2026-10-20T00:00"
const dateTimeToInput = (value?: string | null) => value ? value.slice(0, 16) : ''
const dateTimeToVrbo = (value: string) => value.length === 16 ? `${value}:00` : value

export function promotionToFormValues(promotion: VrboPromotion): VrboPromotionFormValues {
    const values = emptyPromotionFormValues()
    const discountIncentive = promotion.incentive?.discountIncentive
    const discount = discountIncentive?.discount
    const restrictions = promotion.restrictions

    values.promotionType = promotion.promotionType
    values.status = promotion.status

    if (discount?.dayOfWeek) {
        values.discountMode = 'dayOfWeek'
        DAYS_OF_WEEK.forEach(day => {
            values.dayOfWeek[day] = discount.dayOfWeek?.[day]?.value ?? ''
        })
        values.discountUnit = discount.dayOfWeek.monday?.unit || 'PERCENT'
    } else if (discount?.tiered) {
        values.discountMode = 'tiered'
        const tiers = discount.tiered.tiers || []
        tiers.forEach(tier => {
            values.tiers[tier.memberTier] = tier.discount?.value ?? ''
        })
        values.discountUnit = tiers[0]?.discount?.unit || 'PERCENT'
    } else if (discount?.single) {
        values.discountMode = 'single'
        values.singleValue = discount.single.singleDiscount?.value ?? ''
        values.discountUnit = discount.single.singleDiscount?.unit || 'PERCENT'
    }

    values.travelFrom = restrictions?.travelDateRange?.from || ''
    values.travelTo = restrictions?.travelDateRange?.to || ''
    values.blackoutDates = (restrictions?.blackoutDates || []).map(range => ({
        from: range.travelDateFrom,
        to: range.travelDateTo,
    }))
    values.stayMin = restrictions?.stayDurationRange?.min ?? ''
    values.stayMax = restrictions?.stayDurationRange?.max ?? ''
    values.advanceMin = restrictions?.advanceBookingDaysRange?.min ?? ''
    values.advanceMax = restrictions?.advanceBookingDaysRange?.max ?? ''
    values.bookingFrom = dateTimeToInput(restrictions?.bookingLocalDateTimeRange?.from)
    values.bookingTo = dateTimeToInput(restrictions?.bookingLocalDateTimeRange?.to)

    const audienceTarget = (restrictions?.audienceTarget || []).filter(Boolean) as string[]
    if (audienceTarget.length) values.extras.audienceTarget = audienceTarget
    if (restrictions?.sameDayBookingStartTime) values.extras.sameDayBookingStartTime = restrictions.sameDayBookingStartTime
    if (discountIncentive?.applicabilityRules?.applicableNights) {
        values.extras.applicableNights = discountIncentive.applicabilityRules.applicableNights
    }

    return values
}

function buildIncentive(values: VrboPromotionFormValues) {
    const toDiscountValue = (value: string | number) => ({ unit: values.discountUnit, value: String(value) })
    const discount: any = {}

    if (values.discountMode === 'single') {
        discount.single = { singleDiscount: toDiscountValue(values.singleValue) }
    } else if (values.discountMode === 'dayOfWeek') {
        discount.dayOfWeek = {}
        DAYS_OF_WEEK.forEach(day => {
            discount.dayOfWeek[day] = toDiscountValue(values.dayOfWeek[day])
        })
    } else {
        discount.tiered = {
            tiers: MEMBER_TIERS
                .filter(tier => !isBlank(values.tiers[tier]))
                .map(tier => ({ memberTier: tier, discount: toDiscountValue(values.tiers[tier]) })),
        }
    }

    const discountIncentive: any = { discount }
    if (values.extras.applicableNights) {
        discountIncentive.applicabilityRules = { applicableNights: values.extras.applicableNights }
    }
    return { discountIncentive }
}

function buildRestrictions(values: VrboPromotionFormValues, mode: 'create' | 'update') {
    const restrictions: any = {}

    if (values.travelFrom && values.travelTo) {
        restrictions.travelDateRange = { from: values.travelFrom, to: values.travelTo }
    }
    if (!isBlank(values.stayMax)) {
        restrictions.stayDurationRange = { max: Number(values.stayMax) }
        if (!isBlank(values.stayMin)) restrictions.stayDurationRange.min = Number(values.stayMin)
    }
    if (!isBlank(values.advanceMax)) {
        restrictions.advanceBookingDaysRange = { max: Number(values.advanceMax) }
        if (!isBlank(values.advanceMin)) restrictions.advanceBookingDaysRange.min = Number(values.advanceMin)
    }
    if (values.bookingFrom || values.bookingTo) {
        restrictions.bookingLocalDateTimeRange = {}
        if (values.bookingFrom) restrictions.bookingLocalDateTimeRange.from = dateTimeToVrbo(values.bookingFrom)
        if (values.bookingTo) restrictions.bookingLocalDateTimeRange.to = dateTimeToVrbo(values.bookingTo)
    }

    const blackoutDates = values.blackoutDates
        .filter(range => range.from && range.to)
        .map(range => ({ travelDateFrom: range.from, travelDateTo: range.to }))
    // an update sends an empty list so removed blackout dates are actually cleared
    if (blackoutDates.length || mode === 'update') restrictions.blackoutDates = blackoutDates

    if (values.extras.audienceTarget?.length) restrictions.audienceTarget = values.extras.audienceTarget
    if (values.extras.sameDayBookingStartTime) restrictions.sameDayBookingStartTime = values.extras.sameDayBookingStartTime

    return restrictions
}

export function buildCreatePromotionInput(values: VrboPromotionFormValues) {
    return {
        status: values.status,
        promotionType: values.promotionType,
        incentive: buildIncentive(values),
        restrictions: buildRestrictions(values, 'create'),
    }
}

// Only sends the sections that differ from the promotion as it was loaded
export function buildUpdatePromotionInput(initial: VrboPromotionFormValues, current: VrboPromotionFormValues) {
    const input: any = {}
    if (initial.status !== current.status) {
        input.status = current.status
    }

    const incentive = buildIncentive(current)
    if (JSON.stringify(buildIncentive(initial)) !== JSON.stringify(incentive)) {
        input.incentive = incentive
    }

    const restrictions = buildRestrictions(current, 'update')
    if (JSON.stringify(buildRestrictions(initial, 'update')) !== JSON.stringify(restrictions)) {
        input.restrictions = restrictions
    }
    return input
}

const formatDiscountValue = (discount?: VrboDiscountValue | null) => {
    if (!discount) return '-'
    const value = Number(discount.value)
    return discount.unit === 'PERCENT' ? `${value}%` : `$${value}`
}

export function summarizeDiscount(promotion: VrboPromotion): string {
    const discount = promotion.incentive?.discountIncentive?.discount
    if (discount?.single) {
        return `${formatDiscountValue(discount.single.singleDiscount)} off`
    }
    if (discount?.dayOfWeek) {
        const dayValues = Array.from(new Set(DAYS_OF_WEEK.map(day => formatDiscountValue(discount.dayOfWeek?.[day]))))
        return `By day of week (${dayValues.join(', ')})`
    }
    if (discount?.tiered?.tiers?.length) {
        return discount.tiered.tiers.map(tier => `${tier.memberTier} ${formatDiscountValue(tier.discount)}`).join(' / ')
    }
    return '-'
}

function discountValueError(value: string | number, unit: VrboDiscountUnit): string | null {
    if (isBlank(value)) return 'Required'
    const numeric = Number(value)
    if (isNaN(numeric) || numeric <= 0) return 'Must be greater than 0'
    if (unit === 'PERCENT' && numeric > 100) return 'Percent cannot exceed 100'
    return null
}

function rangeSchema(
    label: string,
    minKey: string,
    maxKey: string,
    extraMaxTest?: (this: yup.TestContext, value: any) => boolean | yup.ValidationError,
) {
    let maxSchema = yup.mixed().test(`${maxKey}-required`, `${label} max is required when min is set`, function (value) {
        return !(isBlank(value) && !isBlank(this.parent[minKey]))
    }).test(`${maxKey}-gte-min`, `${label} max must be at least min`, function (value) {
        const min = this.parent[minKey]
        return isBlank(value) || isBlank(min) || Number(value) >= Number(min)
    })
    if (extraMaxTest) {
        maxSchema = maxSchema.test(`${maxKey}-extra`, 'Invalid value', extraMaxTest)
    }
    return { [maxKey]: maxSchema }
}

function advanceCoversTravelTest(this: yup.TestContext, value: any) {
    const { bookingTo, travelFrom } = this.parent
    if (isBlank(value) || !bookingTo || !travelFrom || isNaN(Number(value))) return true
    const latest = latestTravelStart(bookingTo, Number(value))
    if (travelFrom <= latest) return true
    return this.createError({
        message: `Travel start cannot be after the booking window end plus max days ahead (${latest}). Increase max days ahead, extend the booking window end, or move the travel start earlier`,
    })
}

// VRBO rejects travelFrom > bookingTo (date part) + max advance booking days
function latestTravelStart(bookingTo: string, advanceMax: number): string {
    const [year, month, day] = bookingTo.slice(0, 10).split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day + advanceMax)).toISOString().slice(0, 10)
}

export function buildPromotionValidationSchema(isEdit: boolean) {
    return yup.object({
        promotionType: isEdit ? yup.string() : yup.string().required('Promotion type is required'),
        status: yup.string().required('Status is required'),
        travelFrom: yup.string().required('Travel start date is required'),
        travelTo: yup.string().required('Travel end date is required').test(
            'travel-order',
            'Travel end must be on or after travel start',
            function (value) {
                const from = this.parent.travelFrom
                return !value || !from || value >= from
            },
        ),
        ...rangeSchema('Stay duration', 'stayMin', 'stayMax'),
        ...rangeSchema('Advance booking days', 'advanceMin', 'advanceMax', advanceCoversTravelTest),
        bookingTo: yup.string().test(
            'booking-order',
            'Booking window end must be after start',
            function (value) {
                const from = this.parent.bookingFrom
                return !value || !from || value >= from
            },
        ),
        blackoutDates: yup.array().of(yup.object({
            from: yup.string().required('Required'),
            to: yup.string().required('Required').test(
                'blackout-order',
                'End must be on or after start',
                function (value) {
                    const from = this.parent.from
                    return !value || !from || value >= from
                },
            ),
        })),
    }).test('discount', 'Invalid discount', function (values: any) {
        const mode: VrboDiscountMode = values.discountMode
        const unit: VrboDiscountUnit = values.discountUnit

        if (mode === 'single') {
            const message = discountValueError(values.singleValue, unit)
            return message ? this.createError({ path: 'singleValue', message }) : true
        }
        if (mode === 'dayOfWeek') {
            for (const day of DAYS_OF_WEEK) {
                const message = discountValueError(values.dayOfWeek[day], unit)
                if (message) return this.createError({ path: `dayOfWeek.${day}`, message })
            }
            return true
        }

        const filledTiers = MEMBER_TIERS.filter(tier => !isBlank(values.tiers[tier]))
        if (!filledTiers.length) {
            return this.createError({ path: 'discountMode', message: 'Enter a discount for at least one member tier' })
        }
        for (const tier of filledTiers) {
            const message = discountValueError(values.tiers[tier], unit)
            if (message) return this.createError({ path: `tiers.${tier}`, message })
        }
        return true
    })
}
